<template>
    <!--
    We put the slotted trigger inside a <span> so we can:
      - Attach mouseenter/mouseleave listeners
      - Grab a ref for measuring its bounding rect
    Using "display: contents" means this <span> won't add any layout box.
  -->
    <span
        ref="trigger"
        :class="[common.triggerElement, triggerClass]"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        @focus="showTooltip"
        @blur="hideTooltip"
    >
        <slot />
    </span>

    <teleport
        v-if="isContentShown"
        to="body"
    >
        <div
            ref="tooltip"
            :class="[$style.tooltip, common.tooltip, $style[`tooltip-${adjustedPosition}`]]"
            :style="tooltipStyle"
        >
            {{ content }}
            <svg
                width="28"
                height="10"
                viewBox="0 0 28 10"
                fill="none"
            >
                <path
                    d="M0 0L27.997 0.000769947C20.9992 0.00115493 18.3931 4.00878 14.0008 10C9.60586 4.00802 6.78923 0.000172349 0 0Z"
                    fill="var(--electric-lime)"
                />
            </svg>
        </div>
    </teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { useAppStore } from '@/store/app-store'

import common from './Tooltip.module.css'
import { Position, useTooltip } from './use-tooltip'

const {
    content,
    position = 'top',
    offset = 8,
    triggerClass,
    showOnMobile = false,
} = defineProps<{
    content?: string
    position?: Position
    offset?: number
    triggerClass?: string
    showOnMobile?: boolean
}>()

const appStore = useAppStore()
const { platform } = storeToRefs(appStore)

const trigger = ref<HTMLElement | null>(null)
const tooltip = ref<HTMLElement | null>(null)

const { isVisible, showTooltip, hideTooltip, adjustedPosition, tooltipStyle } = useTooltip({
    trigger,
    hideDelayMs: 100,
    tooltip,
    additionalOffset: offset,
    position,
})

defineExpose({
    showTooltip,
    hideTooltip,
})

const isContentShown = computed(() => content && (showOnMobile || platform.value === 'desktop') && isVisible.value)
</script>

<style module>
.tooltip {
    pointer-events: none;
    padding: 10px;
    border-radius: 12px;
    width: max-content;
    max-width: 400px;
    background: var(--electric-lime);
    color: var(--black);
    font-size: 14px;
    line-height: 120%;
}

.tooltip svg {
    position: absolute;
}

.tooltip-top svg {
    bottom: -9px;
    left: calc(50% - 14px);
}

.tooltip-bottom svg {
    top: -9px;
    left: calc(50% - 14px);
    transform: rotate(180deg);
}

.tooltip-left svg {
    top: calc(50% - 5rem);
    right: -18px;
    transform: rotate(-90deg);
}

.tooltip-right svg {
    top: calc(50% - 5rem);
    left: -18px;
    transform: rotate(90deg);
}
</style>
